import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  getHudState,
  isQuietActive,
  sendHudMessage,
  setHudSettings,
  HudMessage,
  HudMessageStatus,
  HudSettings,
  HudState,
  SendHudMessageInput,
} from '../../services/hud';
import { HudStatusStrip, QuietBanner } from './HudStatusStrip';
import { HudComposer } from './HudComposer';
import { MessageHistory, OnGlassesNow, WaitingMessages } from './HudMessages';
import { HudTimeline } from './HudTimeline';
import { errorMessage, isWaiting, messageOutcome, toMs } from './hudShared';
import SteerBox from './SteerBox';
import SessionControls from './SessionControls';
import QueuePanel from './QueuePanel';
import GoalModesPanel from './GoalModesPanel';
import PlanOfRecordPanel from './PlanOfRecordPanel';
import HealthLinePanel from './HealthLinePanel';

interface FacilitatorHudViewProps {
  sessionId: string;
  canEdit?: boolean;
}

const POLL_MS = 10_000;
const SLOW_TICK_MS = 5_000;
const CHIME_KEY = 'facilitatorHud.chime';
const TOAST_MS = 12_000;

let audioCtx: AudioContext | null = null;

function playChime(outcome: 'yes' | 'no' | 'expired') {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx ?? new Ctx();
    const ctx = audioCtx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const NOTES = { yes: [660, 880], no: [660, 440], expired: [440] };
    const notes = NOTES[outcome];
    notes.forEach((freq, i) => {
      const start = ctx.currentTime + i * 0.14;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.13);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.14);
    });
  } catch {
    // Audio is optional.
  }
}

function readChimePref(): boolean {
  try {
    return localStorage.getItem(CHIME_KEY) !== 'off';
  } catch {
    return true;
  }
}

const FacilitatorHudView: React.FC<FacilitatorHudViewProps> = ({ sessionId, canEdit = true }) => {
  const [state, setState] = useState<HudState | null>(null);
  const [pending, setPending] = useState<HudMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingQuiet, setSavingQuiet] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [chime, setChime] = useState(readChimePref);

  const inFlight = useRef(false);
  const sentIds = useRef<Set<string>>(new Set());
  const lastStatus = useRef<Map<string, HudMessageStatus>>(new Map());
  const notified = useRef<Set<string>>(new Set());
  const chimeRef = useRef(chime);
  chimeRef.current = chime;

  const notifyAnswers = useCallback((messages: HudMessage[]) => {
    const prev = lastStatus.current;
    for (const m of messages) {
      const before = prev.get(m.messageId);
      const changed = before !== undefined && before !== m.status;
      const mine = sentIds.current.has(m.messageId);
      if (m.status !== 'waiting' && !notified.current.has(m.messageId) && (changed || (mine && before === undefined))) {
        notified.current.add(m.messageId);
        const outcome = messageOutcome(m);
        if (outcome === 'yes') toast.success(`Lead answered YES: ${m.text}`, { duration: TOAST_MS });
        else if (outcome === 'no') toast.error(`Lead answered NO: ${m.text}`, { duration: TOAST_MS });
        else toast.warning(`No reply: ${m.text}`, { duration: TOAST_MS });
        if (chimeRef.current) playChime(outcome);
      }
    }
    lastStatus.current = new Map(messages.map(m => [m.messageId, m.status]));
  }, []);

  const fetchState = useCallback(async (silent = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (!silent) setRefreshing(true);
    try {
      const data = await getHudState(sessionId);
      notifyAnswers(data.messages);
      setState(data);
      setLastUpdated(Date.now());
      setError(null);
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to load the facilitator HUD'));
    } finally {
      inFlight.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [sessionId, notifyAnswers]);

  useEffect(() => {
    fetchState(true);
    const interval = setInterval(() => {
      if (!document.hidden) fetchState(true);
    }, POLL_MS);
    const onVisible = () => { if (!document.hidden) fetchState(true); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchState]);

  const messages = useMemo(() => {
    const server = state?.messages ?? [];
    return [...pending, ...server];
  }, [pending, state?.messages]);

  const settings: HudSettings = state?.settings ?? { quiet: false };
  const quiet = isQuietActive(settings, now);
  const timedQuiet = quiet && !!settings.quietUntil;
  const hasCountdown = messages.some(m => m.status === 'waiting' && toMs(m.expiresAt) > now) || timedQuiet;

  // 1 s ticker only while a countdown is on screen; otherwise a slow tick for "updated Xs ago" and sync ages.
  useEffect(() => {
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), hasCountdown ? 1000 : SLOW_TICK_MS);
    return () => clearInterval(tick);
  }, [hasCountdown]);

  const waiting = useMemo(
    () => messages.filter(m => isWaiting(m, now)).sort((a, b) => toMs(a.expiresAt) - toMs(b.expiresAt)),
    [messages, now],
  );

  const handleSend = useCallback(async (input: SendHudMessageInput): Promise<boolean> => {
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const created = new Date();
    const temp: HudMessage = {
      messageId: tempId,
      text: input.text,
      detail: input.detail,
      sentBy: '',
      sentByName: 'You',
      createdAt: created.toISOString(),
      expiresAt: new Date(created.getTime() + (input.expiresInMinutes ?? 5) * 60_000).toISOString(),
      response: null,
      status: 'waiting',
    };
    setPending(prev => [temp, ...prev]);
    try {
      const msg = await sendHudMessage(sessionId, input);
      sentIds.current.add(msg.messageId);
      if (!lastStatus.current.has(msg.messageId)) lastStatus.current.set(msg.messageId, msg.status);
      setState(prev => prev
        ? { ...prev, messages: [msg, ...prev.messages.filter(m => m.messageId !== msg.messageId)] }
        : prev);
      return true;
    } catch (err: any) {
      toast.error(errorMessage(err, 'Failed to send message'));
      return false;
    } finally {
      setPending(prev => prev.filter(m => m.messageId !== tempId));
    }
  }, [sessionId]);

  const applyQuiet = useCallback(async (next: boolean, minutes: number | null) => {
    if (!state || savingQuiet) return;
    const previous = state.settings;
    const optimistic: HudSettings = {
      quiet: next,
      quietUntil: next && minutes ? new Date(Date.now() + minutes * 60_000).toISOString() : null,
    };
    setSavingQuiet(true);
    setState(prev => prev ? { ...prev, settings: optimistic } : prev);
    try {
      const saved = await setHudSettings(sessionId, next && minutes ? { quiet: true, quietMinutes: minutes } : { quiet: next });
      setState(prev => prev ? { ...prev, settings: saved } : prev);
    } catch (err: any) {
      setState(prev => prev ? { ...prev, settings: previous } : prev);
      toast.error(errorMessage(err, 'Failed to update quiet mode'));
    } finally {
      setSavingQuiet(false);
    }
  }, [state, savingQuiet, sessionId]);

  const toggleChime = () => {
    setChime(c => {
      const next = !c;
      try { localStorage.setItem(CHIME_KEY, next ? 'on' : 'off'); } catch {}
      if (next) playChime('yes');
      return next;
    });
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-4">
        <HudStatusStrip
          devices={state?.devices ?? []}
          now={now}
          lastUpdated={lastUpdated}
          refreshing={refreshing}
          onRefresh={() => fetchState()}
          quiet={quiet}
          savingQuiet={savingQuiet}
          onQuiet={minutes => applyQuiet(true, minutes)}
          onResume={() => applyQuiet(false, null)}
          chime={chime}
          onToggleChime={toggleChime}
        />

        {quiet && (
          <QuietBanner
            quietUntil={settings.quietUntil}
            now={now}
            saving={savingQuiet}
            onResume={() => applyQuiet(false, null)}
          />
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <Loader2 size={32} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-slate-500 text-sm font-medium">Loading the facilitator HUD...</p>
          </div>
        ) : state && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <div className="flex flex-col gap-4">
              <div className="rounded-xl bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 flex flex-col gap-4">
                <SessionControls sessionId={sessionId} canEdit={canEdit} source="hud" />
                <div className="border-t border-[#e2e8f0] pt-3">
                  <span className="block text-[11px] font-bold text-primary tracking-widest uppercase mb-2">Steer the platform</span>
                  <SteerBox sessionId={sessionId} source="hud" canEdit={canEdit} />
                </div>
              </div>
              <HealthLinePanel sessionId={sessionId} />
              <div className="rounded-xl bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200">
                <QueuePanel sessionId={sessionId} canEdit={canEdit} />
              </div>
              <GoalModesPanel sessionId={sessionId} canEdit={canEdit} />
              <PlanOfRecordPanel sessionId={sessionId} />
              <HudComposer onSend={handleSend} />
              <WaitingMessages messages={waiting} now={now} />
              <OnGlassesNow hints={state.hints} waiting={waiting} quiet={quiet} />
              <MessageHistory messages={messages} now={now} />
            </div>
            <HudTimeline hints={state.hints} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FacilitatorHudView;
